import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { ShoppingBag } from 'lucide-react';
import toast from 'react-hot-toast';
import { TOAST_STYLE, TOAST_SUCCESS_ICON } from '../toast/AppToaster';
import { useCartStore } from '../../store/useCartStore';
import { usePaymentStore } from '../../store/paymentStore';
import { useUIStore } from '../../store/useUIStore';
import { useProductStore, Product } from '../../store/useProductStore';
import { useStoreInfo } from '../../store/useStoreInfo';
import { eventHub } from '../../lib/tracking';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';
import * as S from './styles';

/** نسبة ظهور البطاقة في الشاشة لتُعدّ مشاهدة محتوى (ViewContent) */
const VIEWPORT_IMPRESSION_THRESHOLD = 0.35;

type ProductGridItemWithImpressionProps = {
  product: Product;
  onScrollImpression: (product: Product) => void;
  children: React.ReactNode;
};

function ProductGridItemWithImpression({
  product,
  onScrollImpression,
  children,
}: ProductGridItemWithImpressionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const productRef = useRef(product);
  productRef.current = product;

  useEffect(() => {
    const root = containerRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= VIEWPORT_IMPRESSION_THRESHOLD) {
            io.unobserve(root);
            onScrollImpression(productRef.current);
            break;
          }
        }
      },
      { root: null, rootMargin: '0px', threshold: VIEWPORT_IMPRESSION_THRESHOLD }
    );

    io.observe(root);
    return () => io.disconnect();
  }, [product.id, onScrollImpression]);

  return <div ref={containerRef}>{children}</div>;
}

// مكون الهيكل العظمي للمنتج
const ProductCardSkeleton = () => (
  <S.SkeletonCard>
    <S.SkeletonImage />
    <S.SkeletonDetails>
      <S.SkeletonTitle />
      <S.SkeletonDesc />
      <S.SkeletonPrice />
    </S.SkeletonDetails>
    <S.SkeletonAction>
      <S.SkeletonButton />
    </S.SkeletonAction>
  </S.SkeletonCard>
);

// عرض عدة هياكل عظمية
const ProductListSkeleton = ({ count = 4 }: { count?: number }) => (
  <>
    {Array.from({ length: count }).map((_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </>
);

type ProductListProps = {
  /** عند التعيين، يُفتح modal المنتج بهذا المعرّف عند توفر المنتجات (مفيد لرابط /product/:id) */
  openProductId?: string;
};

const ProductList = ({ openProductId }: ProductListProps) => {
  const addItem = useCartStore((state) => state.addItem);
  const setOrder = usePaymentStore((state) => state.setOrder);
  const { activeFilter } = useUIStore();
  const { products, isLoading, error, fetchProducts } = useProductStore();
  const { data: storeData, fetchStoreData } = useStoreInfo();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  /** يمنع تكرار VIEW_CONTENT لنفس المنتج حتى تتغيّر القائمة/الفلتر (تمرير، نقرة، ديب لينك) */
  const viewContentSentProductIdsRef = useRef<Set<number>>(new Set());
  /** يمنع تكرار view_item_list لنفس القائمة (فلتر + مجموعة المعرّفات) */
  const viewItemListSentSigRef = useRef<string | null>(null);

  // تحميل المنتجات مرة واحدة فقط عند التحميل الأول
  useEffect(() => {
    fetchProducts();
    fetchStoreData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        if (activeFilter === 'offers') {
          return product.originalPrice !== undefined;
        }
        return true;
      }),
    [products, activeFilter]
  );

  const impressionListSig = useMemo(() => {
    const idsKey = filteredProducts
      .map((p) => p.id)
      .sort((a, b) => a - b)
      .join(',');
    return `${activeFilter}:${idsKey}`;
  }, [filteredProducts, activeFilter]);

  useEffect(() => {
    viewContentSentProductIdsRef.current = new Set();
  }, [impressionListSig]);

  const sendViewContentIfNew = useCallback(
    (
      product: Product,
      extra: { location: string; interaction_type: string }
    ) => {
      if (viewContentSentProductIdsRef.current.has(product.id)) return;
      viewContentSentProductIdsRef.current.add(product.id);
      void eventHub.viewContent({
        item_id: product.id.toString(),
        item_name: product.title,
        price: product.price,
        currency: 'SAR',
        content_type: 'product',
        content_name: product.title,
        content_category: product.category || 'Sweets',
        ...extra,
      });
    },
    []
  );

  const onScrollImpression = useCallback(
    (product: Product) => {
      sendViewContentIfNew(product, {
        location: 'product_grid',
        interaction_type: 'scroll_impression',
      });
    },
    [sendViewContentIfNew]
  );

  // فتح modal عند /product/:id + VIEW_CONTENT مرة لكل منتج ضمن سياسة التكرار الحالية
  useEffect(() => {
    if (!openProductId) return;
    if (isLoading || products.length === 0) return;
    const id = parseInt(openProductId, 10);
    if (Number.isNaN(id)) return;
    const product = products.find((p) => p.id === id);
    if (product) {
      setSelectedProduct(product);
      setIsModalOpen(true);
      sendViewContentIfNew(product, {
        location: 'product_modal',
        interaction_type: 'deeplink',
      });
    }
  }, [openProductId, isLoading, products, sendViewContentIfNew]);

  // تحديث عنوان الصفحة في المتصفح بناءً على الفلتر الحالي واسم المتجر
  const storeName = storeData?.name || 'اطلب';
  useEffect(() => {
    const title = activeFilter === 'offers' ? `العروض - ${storeName}` : storeName;
    document.title = title;
  }, [activeFilter, storeName]);

  const sectionTitle = activeFilter === 'offers' ? 'العروض' : 'منتجاتنا';

  // GA4 view_item_list: مرة واحدة عند توفر قائمة المنتجات (بدون تكرار عند نفس الفلتر ونفس المعرّفات)
  useEffect(() => {
    if (isLoading || filteredProducts.length === 0) return;
    const idsKey = filteredProducts
      .map((p) => p.id)
      .sort((a, b) => a - b)
      .join(',');
    const sig = `${activeFilter}:${idsKey}`;
    if (viewItemListSentSigRef.current === sig) return;
    viewItemListSentSigRef.current = sig;

    eventHub.viewItemList({
      item_list_id: activeFilter === 'offers' ? 'offers' : 'all_products',
      item_list_name: sectionTitle,
      items: filteredProducts.map((p) => ({
        item_id: p.id.toString(),
        item_name: p.title,
        price: p.price,
        quantity: 1,
        item_category: p.category || 'Sweets',
      })),
      currency: 'SAR',
      location: 'product_grid',
    });
  }, [isLoading, filteredProducts, activeFilter, sectionTitle]);

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    sendViewContentIfNew(product, {
      location: 'product_modal',
      interaction_type: 'click',
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleAddToCart = async (product: Product) => {
    await addItem({
      id: product.id.toString(),
      title: product.title,
      price: product.price,
      image: product.image,
      description: product.description
    });
    
    setOrder({
      amount: product.price,
      shipping: storeData?.shipping ?? 0,
      qty: 1,
      product: {
        title: product.title,
        image: product.image,
        description: product.description
      }
    });

    toast.success(`تمت إضافة ${product.title} للسلة`, {
      id: `add-${product.id}`,
      style: TOAST_STYLE,
      iconTheme: TOAST_SUCCESS_ICON,
    });
  };

  if (isLoading) {
    return (
      <S.ProductsSection>
        <S.SectionHeader>
          <S.SkeletonTitle style={{ width: '120px', height: '20px' }} />
        </S.SectionHeader>
        <S.ProductsGrid>
          <ProductListSkeleton count={4} />
        </S.ProductsGrid>
      </S.ProductsSection>
    );
  }

  if (error) {
    return (
      <S.ProductsSection>
        <S.SectionHeader>
          <S.SectionTitle>{sectionTitle}</S.SectionTitle>
        </S.SectionHeader>
        <S.EmptyStateContainer>
          <S.EmptyStateText style={{ color: 'oklch(0.4437 0.1613 26.9)', marginBottom: 12 }}>{error}</S.EmptyStateText>
          <button
            type="button"
            onClick={() => fetchProducts(true)}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: '1px solid oklch(0.3211 0 0)',
              background: 'oklch(1 0 0)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            إعادة المحاولة
          </button>
        </S.EmptyStateContainer>
      </S.ProductsSection>
    );
  }

  return (
    <S.ProductsSection>
      <S.SectionHeader>
        <S.SectionTitle>{sectionTitle}</S.SectionTitle>
      </S.SectionHeader>
      
      <S.ProductsGrid>
        {filteredProducts.length === 0 ? (
          <S.EmptyStateContainer>
            <ShoppingBag size={48} color="oklch(0.869 0.0198 252.89)" strokeWidth={1.5} />
            <S.EmptyStateText>لا توجد منتجات في هذا القسم حالياً</S.EmptyStateText>
          </S.EmptyStateContainer>
        ) : (
          filteredProducts.map((product) => (
            <ProductGridItemWithImpression
              key={`${impressionListSig}-${product.id}`}
              product={product}
              onScrollImpression={onScrollImpression}
            >
              <ProductCard
                product={product}
                onAdd={handleAddToCart}
                onClick={handleProductClick}
              />
            </ProductGridItemWithImpression>
          ))
        )}
      </S.ProductsGrid>

      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onAddToCart={handleAddToCart}
      />
    </S.ProductsSection>
  );
};

export default ProductList;
